import { IconChevronChevronLeft } from '@doltech/icons/IconChevronChevronLeft';
import { IconChevronChevronRight } from '@doltech/icons/IconChevronChevronRight';
import { Spin } from 'antd';
import cl from 'classnames';
import { observer } from 'mobx-react-lite';
import * as React from 'react';
import { useClickAway } from 'react-use';
import { Transforms } from 'slate';
import { ReactEditor, useSlateStatic } from 'slate-react';
import styled from 'styled-components';

import { colorsV2 } from '../../../../colors-v2';
import { CardVocabNews } from '../../../../DailyLearning/UserView/CardVocabNews';
import { useSingleCardSwiperStore } from '../../../../DailyLearning/UserView/single-card-swiper.store';
import { SingleCardSwiper } from '../../../../DailyLearning/UserView/SingleCardSwiper';
import { Typography } from '../../../../DesignSystemV2/Typography';
import { DeleteElementButtonAndActions } from '../../../shared-components/DeleteElementButtonAndActions';
import { ieltsCalendarComputedBasedTransforms } from '../../SpecialObject/IeltsCalendar/ComputerBased/transforms';
import { fetchVocabViaUrl } from './UploadVocabListNews';
import { escapeCSS } from '@doltech/utils/lib/object';

const Main = styled.div`
  position: relative;
  .single-card-swiper {
    background: ${colorsV2.white100};
    border: 1px solid ${colorsV2.gray40};
    box-shadow: 0 8px 22px rgba(0, 0, 0, 0.1);
    border-radius: 8px;
    padding: 22px 24px;
  }
  .hidden {
    display: none;
  }
`;

const VocabHeaderMain = styled.div`
  .header {
    display: grid;
    grid-template-columns: minmax(0, 1fr) max-content;
    margin-bottom: 10px;
  }
  .header-title {
    font-size: 14px;
    font-weight: 600;
    line-height: 20px;
    color: ${colorsV2.black70};
  }
  .header-actions {
    display: grid;
    grid-auto-flow: column;
    grid-auto-columns: max-content;
    gap: 16px;
  }
  .swiper-navigation {
    width: 32px;
    height: 32px;
    min-width: 32px;
    min-height: 32px;
    border-radius: 50%;
    display: grid;
    place-items: center;
    line-height: 0;
    border: 1px solid ${colorsV2.black100};
    color: ${colorsV2.black100};
    font-size: 24px;
    transition: all 0.3s ease;
    cursor: pointer;
    &.disabled {
      opacity: 0.4;
      cursor: not-allowed;
    }
    &:hover {
      transform: scale(1.1);
    }
    &:active {
      transform: scale(0.9);
    }
  }
`;

const VocabHeader = observer(({ id, dataSource }: any) => {
  const store = useSingleCardSwiperStore();

  return (
    <VocabHeaderMain>
      <div className="header">
        <div className="header-title">
          VOCAB {store.currentSlideIndex + 1}/{dataSource?.length || 0}
        </div>
        <div className="header-actions">
          <div className={cl('swiper-navigation prev', id)}>
            <IconChevronChevronLeft />
          </div>
          <div className={cl('swiper-navigation next', id)}>
            <IconChevronChevronRight />
          </div>
        </div>
      </div>
    </VocabHeaderMain>
  );
});

export const VocabListNews = (props) => {
  const { element, isPreview, children, ...rest } = props;
  const editor = useSlateStatic();
  const [loading, setLoading] = React.useState(false);
  const [isSelected, setSelected] = React.useState(false);
  const mainRef = React.useRef();
  useClickAway(mainRef, () => setSelected(false));

  return (
    <Main
      id={element.id as string}
      ref={mainRef}
      onClick={() => {
        setSelected(true);
      }}
      contentEditable={false}
      className={cl('vocab-list-news', element.id)}
      {...rest}
    >
      <Spin spinning={loading}>
        {!loading && (
          <SingleCardSwiper
            swiperProps={{ autoHeight: true }}
            id={escapeCSS(element.id)}
            useDefaultCardStyled={false}
            showPagination={false}
            renderHeader={(renderProps) => <VocabHeader {...renderProps} />}
            dataSource={element.vocabs || []}
            renderItem={(vocab) => {
              return (
                <CardVocabNews
                  word={vocab.value}
                  meaning={vocab.meaning}
                  image={{
                    url: vocab.image,
                  }}
                />
              );
            }}
            title="Từ vựng"
          />
        )}
        <div className="hidden">{children}</div>
      </Spin>
      {!isPreview && (
        <DeleteElementButtonAndActions
          actions={[
            {
              icon: (
                <Typography.Text className="font-inter" color="black100" variant="medium/14">
                  Update Data
                </Typography.Text>
              ),
              onClick: async () => {
                setLoading(true);
                try {
                  const res = await fetchVocabViaUrl(element.url);
                  const path = ReactEditor.findPath(editor as any, element);
                  if (res.data) {
                    Transforms.setNodes(editor, res.data, { at: path });
                  }
                  // eslint-disable-next-line no-empty
                } catch (ex) {}
                setLoading(false);
              },
              style: { width: 'auto', fontSize: 14, padding: '6px 20px 6px 8px' },
            },
          ]}
          visible={isSelected}
          onClick={() => ieltsCalendarComputedBasedTransforms.removeNodeById(editor, element.id)}
        />
      )}
    </Main>
  );
};

export const VocabListNewsReadOnly = (props) => {
  const { element, isPreview, children, ...rest } = props;
  const mainRef = React.useRef();

  return (
    <Main
      id={element.id as string}
      ref={mainRef}
      className={cl('vocab-list-news', element.id)}
      {...rest}
    >
      <SingleCardSwiper
        swiperProps={{ autoHeight: true }}
        customWidth={(widthOfBlogEditor) => [
          widthOfBlogEditor ? widthOfBlogEditor - 64 : 'calc(100vw - 64px)',
          widthOfBlogEditor ? widthOfBlogEditor : 532,
          widthOfBlogEditor ? widthOfBlogEditor : 532,
        ]}
        id={escapeCSS(element.id)}
        useDefaultCardStyled={false}
        showPagination={false}
        renderHeader={(renderProps) => <VocabHeader {...renderProps} />}
        dataSource={element.vocabs || []}
        renderItem={(vocab) => {
          return (
            <CardVocabNews
              word={vocab.value}
              meaning={vocab.meaning}
              image={{
                url: vocab.image,
              }}
            />
          );
        }}
        title="Từ vựng"
      />
    </Main>
  );
};
